const PROJECTED_USING_PP_LABEL = "Projected using PP";
const MAX_ACTIVATION_PROFILE_CARDS = 11;
const NEW_CARD_DAY_GAP = 30;

function lastFilledProfileIndex(profiles) {
  for (let i = profiles.length - 1; i >= 0; i--) {
    const profile = profiles[i];
    if (
      profile?.siteActivationProfilePct !== null &&
      profile?.siteActivationProfilePct !== undefined
    ) {
      return i;
    }
  }
  return -1;
}

// Updates an existing final "Projected using PP" card to the new site count.
// Activated and Projected in Remarque cards are left unchanged.
export function updateLastProfileAndApply(profiles, newSitesBoundValue) {
  if (newSitesBoundValue === null || !profiles || profiles.length === 0) {
    return profiles;
  }

  const updatedProfiles = [...profiles];
  const lastProfileIndex = lastFilledProfileIndex(updatedProfiles);

  const lastProfile = updatedProfiles[lastProfileIndex];
  if (lastProfile?.siteActivationLabel !== PROJECTED_USING_PP_LABEL) {
    return profiles;
  }

  updatedProfiles[lastProfileIndex] = {
    ...lastProfile,
    siteActivationProfilePct: newSitesBoundValue,
  };
  return updatedProfiles;
}

export function prepareActivationProfilesForUpdate(profiles, overrideFlag) {
  return profiles.reduce((acc, profile) => {
    if (
      profile.siteActivationProfilePct !== null ||
      profile.siteActivationProfileDays !== null ||
      (typeof profile.siteActivationProfileId === "string" &&
        !profile.siteActivationProfileId.startsWith("empty-"))
    ) {
      acc.push({
        ...profile,
        overrideFl: overrideFlag ? 1 : 0,
      });
    }
    return acc;
  }, []);
}

// Appends a final "Projected using PP" card when an increase has no such card yet.
// The new card uses the new site count even when that count is below the last card.
// Callers stage it for save and do not paint it before the plan refresh.
export function appendProjectedUsingPpCard(profiles, newSitesBoundValue) {
  if (!profiles?.length) return profiles;

  const filledCount = profiles.filter(
    (profile) =>
      profile?.siteActivationProfilePct !== null &&
      profile?.siteActivationProfilePct !== undefined,
  ).length;
  if (filledCount === 0 || filledCount >= MAX_ACTIVATION_PROFILE_CARDS) {
    return profiles;
  }

  const nextCount = Number(newSitesBoundValue);
  if (!Number.isFinite(nextCount)) return profiles;

  const alreadyHasProjectedUsingPp = profiles.some(
    (profile) => profile?.siteActivationLabel === PROJECTED_USING_PP_LABEL,
  );
  if (alreadyHasProjectedUsingPp) return profiles;

  const lastProfileIndex = lastFilledProfileIndex(profiles);
  if (lastProfileIndex < 0) return profiles;

  const lastProfile = profiles[lastProfileIndex];
  const lastDays = Number(lastProfile.siteActivationProfileDays);
  const nextDays = Number.isFinite(lastDays) ? lastDays + NEW_CARD_DAY_GAP : NEW_CARD_DAY_GAP;
  const newCard = {
    siteActivationProfileId: `projected-pp-${lastProfileIndex + 1}`,
    siteActivationLabel: PROJECTED_USING_PP_LABEL,
    siteActivationProfilePct: nextCount,
    siteActivationProfileDays: nextDays,
  };

  const updatedProfiles = [...profiles];
  updatedProfiles.splice(lastProfileIndex + 1, 0, newCard);
  return updatedProfiles;
}

// Builds the activation-profile payload for a confirmed Remodel site-count change.
// Updates an existing final Projected using PP card, or appends one when the site
// count increased and that card was not already present. Returns null otherwise.
export function stageRemodelActivationProfiles(
  profiles,
  newSitesBoundValue,
  previousSitesBoundValue,
) {
  if (
    !profiles?.length ||
    newSitesBoundValue === null ||
    newSitesBoundValue === undefined
  ) {
    return null;
  }

  const hasProfileWithValue = profiles.some(
    (profile) =>
      profile?.siteActivationProfilePct !== null &&
      profile?.siteActivationProfilePct !== undefined,
  );
  if (!hasProfileWithValue) return null;

  let updatedProfiles = updateLastProfileAndApply(profiles, newSitesBoundValue);
  if (JSON.stringify(updatedProfiles) === JSON.stringify(profiles)) {
    const increased = Number(newSitesBoundValue) > Number(previousSitesBoundValue);
    if (!increased) return null;
    updatedProfiles = appendProjectedUsingPpCard(profiles, newSitesBoundValue);
    if (JSON.stringify(updatedProfiles) === JSON.stringify(profiles)) return null;
  }

  return prepareActivationProfilesForUpdate(updatedProfiles, true);
}
